<template>
  <div class="row sort-and-filter mt-3 mb-3">
    <div class="col right">
      <div class="dropdown">
        <button
          class="btn dropdown-toggle"
          type="button"
          id="parkFilterButton"
          data-toggle="dropdown"
          aria-haspopup="true"
          aria-expanded="false"
        >Park Filter</button>
        <div class="dropdown-menu" aria-labelledby="parkFilterButton">
          <button class="dropdown-item" type="button" @click="changeCalendarFilter(null)">All</button>
          <button
            class="dropdown-item"
            type="button"
            v-for="opCal in operatingCalendars"
            :key="opCal.operatingExtCalendarId"
            @click="changeCalendarFilter(opCal.operatingExtCalendarId)"
          >{{ opCal.displayName }}</button>
        </div>
      </div>
    </div>
  </div>
  <div class="row daily-schedule-header">
    <div class="col-md-6 text-left nav-return-month">
      <router-link :to="returnUrl" replace>
        <span class="fas fa-arrow-left mr-1"></span>
        Return to Month View
      </router-link>
    </div>
    <div class="col-md-6 text-right nav-list-by">
      <div class="mobile-only">
        <div class="mt-3"></div>
      </div>
      <div class="d-inline pr-3 list-by-item">
        <span v-if="isByTime" class="active">List By Time</span>
        <router-link v-if="!isByTime" :to="byTimeUrl" replace>List By Time</router-link>
      </div>
      <div class="d-inline ml-3 list-by-item">
        <span v-if="!isByTime" class="active">List By Activity</span>
        <router-link v-if="isByTime" :to="byActivityUrl" replace>List By Activity</router-link>
      </div>
    </div>
  </div>
  <a name="times-list" id="times-list"></a>
  <div class="row" :class="{ 'loading': isLoading }">
    <div class="col-md-6 activity-list">
      <div v-if="activities.featuredActivities.length">
        <div class="events-banner-featured">{{ calendarPageContent.featuredEventsHeadline }}</div>
        <ActivityFeatured
          v-for="schedule in activities.featuredActivities"
          :key="schedule.extCalendarId"
          :activity-day-schedule="schedule"
          @activity-selected="changeSelectedActivity"
        />
      </div>
      <div v-if="activities.nonFeaturedActivities.length && !isByTime">
        <div class="events-banner-all">{{ calendarPageContent.allActivitiesHeadline }}</div>
        <ActivityNonFeatured
          v-for="schedule in activities.nonFeaturedActivities"
          :key="schedule.extCalendarId"
          :activity-day-schedule="schedule"
          @activity-selected="changeSelectedActivity"
        />
      </div>
      <div v-if="activities.nonFeaturedActivityTimes.length && isByTime">
        <div class="events-banner-all">{{ calendarPageContent.allActivitiesHeadline }}</div>
        <ActivityTimeNonFeatured
          v-for="activityTime in activities.nonFeaturedActivityTimes"
          :key="activityTime.sortKey"
          :activity-day-schedule="activityTime.activity"
          :time-slot="activityTime.schedule"
          @activity-selected="changeSelectedActivity"
        />
      </div>
      <div v-if="!hasVisibleActivities">
        <em>No activities are scheduled for this day</em>
      </div>
    </div>
    <div class="col-md-6 activity-detail" v-if="hasVisibleActivities">
      <ActivityDetail
        v-if="selectedActivity !== null && selectedActivitySchedule !== null"
        :activity="selectedActivity!"
        :schedule="selectedActivitySchedule!"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onBeforeMount, inject, computed, ref, toRefs, watch, reactive } from 'vue';
import { DateTime } from 'luxon';
import * as CalendarClient from "../composables/calendar-client";
import * as CalendarModel from "../composables/calendar-model";
import * as ContentModel from "../composables/calendar-content-model";
import CalendarAppParam from "../composables/application-parameter";
import ActivityFeatured from "./ActivityFeatured.vue";
import ActivityNonFeatured from "./ActivityNonFeatured.vue";
import ActivityTimeNonFeatured from "./ActivityTimeNonFeatured.vue";
import ActivityDetail from "./ActivityDetail.vue";
import { ActivityTimeModel } from '../composables/calendar-model';

export default defineComponent({
  components: { ActivityFeatured, ActivityNonFeatured, ActivityTimeNonFeatured, ActivityDetail },
  props: {
    month: { type: Number, required: true },
    day: { type: Number, required: true },
    year: { type: Number, required: true },
    isByTime: { type: Boolean, required: true }
  },
  setup(props, context) {
    // Static Properties
    const contentLookup = inject(ContentModel.ContentLookup)!;
    const appParam = inject(CalendarAppParam)!;
    const filteredExtCalendarId = inject(CalendarModel.FilteredExtCalendarId)!;

    // Reactive Properties
    const refProps = toRefs(props);
    const activityDate = computed(() => 
      DateTime.utc(refProps.year.value, refProps.month.value, refProps.day.value)
        .setZone(appParam.timeZoneName, {keepLocalTime: true})
    );      
    const isLoading = ref(true);
    const activityDateName = computed(() => activityDate.value.toLocaleString({ month: 'long', day: 'numeric' }));
    const activityMonthName = computed(() => activityDate.value.toLocaleString({ month: 'long' }));
    const returnUrl = computed(() => `/${props.year}/${props.month}`);
    const byTimeUrl = computed(() => `/${props.year}/${props.month}/${props.day}/ByTime`);
    const byActivityUrl = computed(() => `/${props.year}/${props.month}/${props.day}/ByActivity`);

    // Reactive Properties: Data Collections
    const allActivities = reactive({
      featuredActivities: [] as CalendarModel.ActivityDayScheduleDto[],
      nonFeaturedActivities: [] as CalendarModel.ActivityDayScheduleDto[],
      nonFeaturedActivityTimes: [] as CalendarModel.ActivityTimeModel[]
    });
    const filteredActivities = ref({
      featuredActivities: [] as CalendarModel.ActivityDayScheduleDto[],
      nonFeaturedActivities: [] as CalendarModel.ActivityDayScheduleDto[],
      nonFeaturedActivityTimes: [] as CalendarModel.ActivityTimeModel[]
    });
    const hasVisibleActivities = computed(() => filteredActivities.value.featuredActivities.length > 0 ||
      (!refProps.isByTime.value && filteredActivities.value.nonFeaturedActivities.length > 0) ||
      (refProps.isByTime.value && filteredActivities.value.nonFeaturedActivityTimes.length > 0));

    // Data setup
    const selectedActivity = ref<ContentModel.CalendarActivityContent | null>(null);
    const selectedActivitySchedule = ref<CalendarModel.ActivityDayScheduleDto | null>(null);

    // Events
    const changeSelectedActivity = function (activity: ContentModel.CalendarActivityContent, schedule: CalendarModel.ActivityDayScheduleDto) {
      selectedActivity.value = activity;
      selectedActivitySchedule.value = schedule;

      // Emulate media query for .mobile-only
      if (window.innerWidth <= 991) {
        document.getElementById("details")?.scrollIntoView({ behavior: "smooth" });
      }
    };
    const changeCalendarFilter = (extCalendarId: string | null) => {
      filteredExtCalendarId.value = extCalendarId;
    };

    // Queries
    const getActivities = async () => {
      isLoading.value = true;
      const featuredActivityResult$ = CalendarClient.getDailyActivities(
        activityDate.value.toISODate()!, 
        contentLookup.value.dailyFeaturedExtCalendarIds);
      const activityResult$ = CalendarClient.getDailyActivities(
        activityDate.value.toISODate()!, 
        contentLookup.value.dailyStandardExtCalendarIds);

      const [featuredActivityResult, activityResult] = await Promise.all([featuredActivityResult$, activityResult$]);

      if (!featuredActivityResult || !activityResult) {
        console.error("Unable to get activites");
        return;
      }

      allActivities.featuredActivities = featuredActivityResult;
      allActivities.nonFeaturedActivities = activityResult;

      // Reorganize events by activity to get events by time
      const flatTimes = activityResult.flatMap(activity => {
        const activityName = contentLookup.value.activityById[activity.extCalendarId].displayName;
        if(activity.isAllDayEvent) {
          return {
            activity,
            schedule: undefined,
            sortKey: `90010|${activityName}`
          } as ActivityTimeModel;
        }
        if(activity.toBeDetermined) {
          return {
            activity,
            schedule: undefined,
            sortKey: `90020|${activityName}`
          } as ActivityTimeModel;
        }
        return activity.schedules.map(schedule => ({
          activity,
          schedule,
          sortKey: `${schedule.sortKey}|${activityName}`
        } as ActivityTimeModel));
      });
      flatTimes.sort((a, b) => a.sortKey === b.sortKey ? 0 : a.sortKey < b.sortKey ? -1 : 1);
      allActivities.nonFeaturedActivityTimes = flatTimes;

      applyCalendarFilter();

      // Remove any loading indicators
      isLoading.value = false;
      context.emit(CalendarModel.Event.DataLoaded, true);
    };

    const applyCalendarFilter = () => {
      if (filteredExtCalendarId.value === null) {
        filteredActivities.value = allActivities;
      } else {
        filteredActivities.value = {
          featuredActivities: allActivities.featuredActivities.filter(o => o.operatingExtCalendarId === filteredExtCalendarId.value),
          nonFeaturedActivities: allActivities.nonFeaturedActivities.filter(o => o.operatingExtCalendarId === filteredExtCalendarId.value),
          nonFeaturedActivityTimes: allActivities.nonFeaturedActivityTimes.filter(o => o.activity.operatingExtCalendarId === filteredExtCalendarId.value)
        };
      }

      ensureSelectedActivity();
    };

    const ensureSelectedActivity = () => {
      const availableActivites = [
        ...filteredActivities.value.featuredActivities,
        ...filteredActivities.value.nonFeaturedActivities.filter(o => !refProps.isByTime.value || o.schedules.length)];
      if (availableActivites.length > 0 &&
        (selectedActivity.value === null || !availableActivites.find(o => o.extCalendarId === selectedActivity.value?.extCalendarId))) {
        changeSelectedActivity(contentLookup.value.activityById[availableActivites[0].extCalendarId], availableActivites[0]);
      }
    };

    // Reactions
    onBeforeMount(getActivities);
    watch(activityDate, getActivities);
    watch(filteredExtCalendarId, applyCalendarFilter);
    watch(refProps.isByTime, ensureSelectedActivity);

    // return Model
    return {
      activityDate,
      activityDateName,
      activityMonthName,
      returnUrl,
      byTimeUrl,
      byActivityUrl,
      activities: filteredActivities,
      selectedActivity,
      selectedActivitySchedule,
      changeSelectedActivity,
      calendarPageContent: appParam,
      isLoading,
      operatingCalendars: computed(() => contentLookup.value.operatingCalendars),
      changeCalendarFilter,
      hasVisibleActivities
    }
  }
});

</script>
